import { Injectable } from '@angular/core';
import { TicketStore } from './ticket.store';
import { Ticket } from './models/ticket.model';
import { tap, mapTo } from 'rxjs/operators';
import { ApiService } from '../../shared/services/core/api.service';

import { TicketQuery } from './ticket.query';
import { Observable, of } from 'rxjs';
import { TicketResponseModel } from './models/ticket-reponse.model';
import { TicketValidateModel } from './models/ticket-validate.model';
import { SubmitTicketRequestModel, TicketTransactionModel } from '../models/ticket-view.model';
import { TicketActionType, TicketFlag } from '../models/ticket.enums';
import { DeliveryParameters } from '../models/ticket-deliveryparams.model';

@Injectable({ providedIn: 'root' })
export class TicketService {
  constructor(private ticketStore: TicketStore, private ticketQuery: TicketQuery, private apiService: ApiService,) {}

  fetchTickets() {
    this.ticketStore.setLoading(true);

    const request$ = this.apiService.get<Ticket>('tickets').pipe(
      tap((tickets) => {
        tickets = tickets.map((ticket) => {
          ticket.Id = `${ticket.ACN}_${ticket.GameNumber}`;
          return ticket;
        });
        this.ticketStore.set(tickets);
      }),
      mapTo(null)
    );

    return this.ticketQuery.getHasCache() ? of(null) : request$;
  }

  fetchTransferHistory(memberId: string, acn: number, gameNumber: number): Observable<TicketTransactionModel[]> {
    return this.apiService.get<TicketTransactionModel>('tickets/log/' + memberId + '/' + acn + '/' + gameNumber);
  }

  toggleSelectTicket(id: string) {
    this.ticketStore.update(id, (ticket) => ({
      selected: !ticket.selected,
    }));
  }

  toggleSelectAllTickets(gameNumber: number, anyUnselected: boolean) {
    this.ticketStore.update(
      (ticket: Ticket) =>
        ticket.GameNumber === gameNumber && ticket.Flag === TicketFlag.ACTIVE,
      {
        selected: anyUnselected,
      }
    );
  }

  removeSelectedTicket(id: string) {
    this.ticketStore.update(id, {
      selected: false,
    });
  }

  // Onload of page no tickets should be selected
  resetSelectedTickets() {
    this.ticketStore.update(null, {
      selected: false,
    });
  }

  resetTransferredTickets() {
    this.ticketStore.update(null, {
      Transferred: false,
    });
  }

  checkFlag(status: number, flag: number) {
    // tslint:disable-next-line: no-bitwise
    return status & flag;
  }

  setTransferredTicket(id: string, emailAddress: string) {
    this.ticketStore.update((ticket: Ticket) => ticket.Id === id, {
      Transferred: true,
      RecipientEmailAddress: emailAddress,
    });
  }

  callbackTicket(ticket: Ticket, deliveryParameters: DeliveryParameters): Observable<TicketResponseModel[] | undefined> {
    this.ticketStore.update((ticket: Ticket) => ticket.Id === ticket.Id, {
      TicketAction: TicketActionType.Callback,
      ShouldUseUniqueDeliveryParameters: true,
      DeliveryParameters: deliveryParameters,
    });

    let objDeliveryParameters = {
      Callback: deliveryParameters,
    };

    let ticketToSubmit = this.ticketQuery.getEntity(ticket.Id);

    if (!ticketToSubmit) {
      return of(undefined);
    }

    let submitTicketRequest = new SubmitTicketRequestModel();
    submitTicketRequest.Tickets = [ticketToSubmit];
    submitTicketRequest.DeliveryParameters = objDeliveryParameters;
    submitTicketRequest.UsePurchaseService = false;

    return this.apiService.post('tickets/submit', submitTicketRequest).pipe(
      tap((result) => {
        if (!result) {
          return;
        }
        result.forEach((response: TicketResponseModel) => {
          if (response.Result.ErrorNumber === 0) {
            this.ticketStore.update(
              (ticket: Ticket) => ticket.ACN === response.Ticket.ACN && ticket.GameNumber === response.Ticket.GameNumber,
              {
                Flag: TicketFlag.ACTIVE,
              }
            );
          }
        });
      })
    );
  }

  transferTicket(deliveryParameters: DeliveryParameters): Observable<TicketResponseModel[]> {
    let objDeliveryParameters = {
      TransferToEmail: deliveryParameters,
    };

    this.ticketStore.update((ticket: Ticket) => ticket.selected, {
      TicketAction: TicketActionType.TransferToEmail,
      ShouldUseUniqueDeliveryParameters: true,
      DeliveryParameters: deliveryParameters,
    });

    let selectedTickets = this.ticketQuery.getAll({ filterBy: (ticket) => ticket.selected });

    let submitTicketRequest = new SubmitTicketRequestModel();
    submitTicketRequest.Tickets = selectedTickets;
    submitTicketRequest.DeliveryParameters = objDeliveryParameters;
    submitTicketRequest.UsePurchaseService = true;

    return this.apiService.post('tickets/submit', submitTicketRequest).pipe(
      tap((result) =>
        result.forEach((response) => {
          this.ticketStore.update(
            (ticket: Ticket) => ticket.ACN === response.Ticket.ACN && ticket.GameNumber === response.Ticket.GameNumber,
            {
              Transferred: true,
              Flag: response.Ticket.Flag,
              TransfereeEmailAddress: response.Ticket.TransfereeEmailAddress,
            }
          );
        })
      )
    );
  }

  /*this method will verify the ticket key*/
  validateTicketInviteKey(ticketKey: string) {
    return this.apiService.get('tickets/pending/' + ticketKey);
  }

  /*this method will verify the email, unique code and ticket key*/

  validateTicketInviteCredentials(ticketRequest: TicketValidateModel) {
    return this.apiService.post('tickets/pending/validate', ticketRequest);
  }

  /*this method will process the all ticket against the email unique code*/
  ticketTransferprocess(ticketRequest: TicketValidateModel) {
    return this.apiService.post('tickets/pending/process', ticketRequest);
  }
}
